import { ethereum } from '@0xtorch/core'
import { mainnet } from 'viem/chains'
import { type Client, createClient } from '../../client'
import type { Explorer } from '../../explorer'
import { createEtherscanV2 } from '../../explorer'
import type { Chain } from '../types/chain'
import type { HttpRpc } from '../types/rpc'

export const createEthereumChain = ({
  explorerApiKey: apiKey,
  explorerProxyUrl: proxyUrl,
  explorerPageSize: pageSize,
}: {
  explorerApiKey?: string
  explorerProxyUrl?: string
  explorerPageSize?: number
}): Chain =>
  createEthereumChainCustom({
    client: createClient({
      chain: mainnet,
      httpRpcs: ethereumHttpRpcs,
    }),
    explorer: createEtherscanV2({
      name: 'Etherscan',
      baseUrl: 'https://etherscan.io',
      chainId: 1,
      apiKey,
      proxyUrl,
      pageSize,
    }),
  })

type CreateEthereumChainParameters = {
  client: Client
  explorer: Explorer
}

export const createEthereumChainCustom = ({
  client,
  explorer,
}: CreateEthereumChainParameters): Chain => ({
  id: 1,
  network: 'ethereum',
  name: 'Ethereum',
  nativeToken: {
    name: 'Ethereum',
    symbol: 'ETH',
    decimals: 18,
    currency: ethereum,
  },
  wrappedTokenAddresses: new Set([
    '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2',
  ]),
  explorer,
  client,
  blockTime: 12_000,
  coingeckoId: 'ethereum',
  defillamaId: 'ethereum',
})

export const ethereumHttpRpcs: HttpRpc[] = [
  {
    url: 'https://go.getblock.io/aefd01aa907c4805ba3c00a9e5b48c6b',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
  // Endpoint is not working
  // {
  //   url: 'https://go.getblock.io/d7dab8149ec04390aaa923ff2768f914',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 20_000n,
  // },
  {
    url: 'https://eth-mainnet.nodereal.io/v1/1659dfb40aa24bbb8153a677b98064d7',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 50_000n,
  },
  {
    url: 'https://ethereum-rpc.publicnode.com',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
  {
    url: 'https://1rpc.io/eth',
    getLogsIsUsable: false,
    getLogsMaxBlockRange: 0n,
  },
  // Endpoint is not working
  // {
  //   url: 'https://rpc.builder0x69.io/',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  {
    url: 'https://rpc.mevblocker.io',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
  {
    url: 'https://rpc.flashbots.net/',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 40_000n,
  },
  // Not archiving receipts
  // {
  //   url: 'https://virginia.rpc.blxrbdn.com/',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Not archiving receipts
  // {
  //   url: 'https://uk.rpc.blxrbdn.com/',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Not archiving receipts
  // {
  //   url: 'https://singapore.rpc.blxrbdn.com/',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Not archiving receipts
  // {
  //   url: 'https://eth.rpc.blxrbdn.com/',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  {
    url: 'https://cloudflare-eth.com/',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 800n,
  },
  // Not archiving receipts
  // {
  //   url: 'https://api.securerpc.com/v1',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // CORS error
  // {
  //   url: 'https://openapi.bitstack.com/v1/wNFxbiJyQsSeLrX8RRCHi7NpRxrlErZk/DjShIqLishPCTB9HiMkPHXjUM9CNM9Na/ETH/mainnet',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  {
    url: 'https://eth-pokt.nodies.app',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
  // Not archiving receipts
  // {
  //   url: 'https://eth-mainnet-public.unifra.io',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  {
    url: 'https://ethereum.public.blockpi.network/v1/rpc/public',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 1024n,
  },
  // Not archiving receipts
  // {
  //   url: 'https://rpc.payload.de',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://api.zmok.io/mainnet/oaen6dy8ff6hju9k',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  {
    url: 'https://eth-mainnet.g.alchemy.com/v2/demo',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
  {
    url: 'https://eth.api.onfinality.io/public',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 2000n,
  },
  // Endpoint is not working
  // {
  //   url: 'https://yolo-intensive-paper.discover.quiknode.pro/45cad3065a05ccb632980a7ee67dd4cbb470ffbd/',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://api.mycryptoapi.com/eth',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://mainnet-nethermind.blockscout.com/',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // CORS error
  // {
  //   url: 'https://nodes.mewapi.io/rpc/eth',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 5000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://main-rpc.linkpool.io/',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://mainnet.eth.cloud.ava.do/',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://ethereumnodelight.app.runonflux.io',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Not archiving receipts
  // {
  //   url: 'https://eth-mainnet.rpcfast.com?api_key=xbhWBI1Wkguk8SNMu1bvvLurPGLXmgwYeC4S6g2H7WdwFigZSmPWVZRxrskEQwIf',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://main-light.eth.linkpool.io',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Not archiving receipts
  // {
  //   url: 'https://rpc.eth.gateway.fm',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://rpc.chain49.com/ethereum?api_key=14d1a8b86d8a4b4797938332394203dc',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Not archiving receipts
  // {
  //   url: 'https://eth.meowrpc.com',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  {
    url: 'https://eth.drpc.org',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
  {
    url: 'https://mainnet.gateway.tenderly.co',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 20_000n,
  },
  // Endpoint is not working
  // {
  //   url: 'https://rpc.tenderly.co/fork/c63af728-a183-4cfb-b24e-a92801463484',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  {
    url: 'https://gateway.tenderly.co/public/mainnet',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 20_000n,
  },
  // Endpoint is not working
  // {
  //   url: 'https://api.zan.top/node/v1/eth/mainnet/public',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://eth-mainnet.diamondswap.org/rpc',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://rpc.notadegen.com/eth',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 20_000n,
  // },
  {
    url: 'https://eth.merkle.io',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
  // Not archiving receipts
  // {
  //   url: 'https://rpc.lokibuilder.xyz/wallet',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://services.tokenview.io/vipapi/nodeservice/eth?apikey=qVHq2o6jpaakcw3lRstl',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // CORS error
  // {
  //   url: 'https://eth.nodeconnect.org/',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://api.stateless.solutions/ethereum/v1/demo',
  //   getLogsIsUsable: false,
  //   getLogsMaxBlockRange: 0n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://rpc.polysplit.cloud/v1/chain/1',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://public.stackup.sh/api/v1/node/ethereum-mainnet',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://ethereum-mainnet.gateway.tatum.io',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'https://eth.nownodes.io',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  // Endpoint is not working
  // {
  //   url: 'rpc.nodifi.ai/api/rpc/free',
  //   getLogsIsUsable: true,
  //   getLogsMaxBlockRange: 10_000n,
  // },
  {
    url: 'https://ethereum.rpc.subquery.network/public',
    getLogsIsUsable: false,
    getLogsMaxBlockRange: 0n,
  },
]

export const ethereumWebsocketRpcUrls: readonly string[] = [
  'wss://ethereum.publicnode.com',
]
